import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Mail, ArrowLeft, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import ThemeSwitch from "@/theme/ThemeSwitch";
import { Card, Field, Input, Button } from "@/ui";

const DEMO = [
  { label: "Shop owner", email: "owner@titababy.ph" },
  { label: "Platform owner", email: "owner@reto.app" },
  { label: "Partner", email: "partner@reto.app" },
  { label: "Helper", email: "helper@reto.app" },
];

export default function Login() {
  const { requestLink, verifyCode, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [stage, setStage] = useState("email"); // email | sent | password
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(params.get("error") === "link" ? "That link didn't work. Ask for a new one below." : "");
  const [resendIn, setResendIn] = useState(0);
  const [devCode, setDevCode] = useState("");

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    const r = await requestLink(email.trim());
    setBusy(false);
    if (r.ok) {
      setStage("sent"); setResendIn(r.resendInSeconds || 30);
      // Email is mocked in this demo: surface the code so sign-in is testable.
      try {
        const dev = await api.get(`/auth/dev/last-code?email=${encodeURIComponent(email.trim())}`);
        if (dev.data && dev.data.code) { setDevCode(dev.data.code); setCode(dev.data.code); }
      } catch {}
    }
    else setError(r.message);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await verifyCode(email.trim(), code.trim());
    setBusy(false);
    if (r.ok) navigate("/today", { replace: true });
    else setError(r.message);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await login(email.trim(), password);
    setBusy(false);
    if (r.ok) navigate("/today", { replace: true });
    else setError(r.message);
  };

  return (
    <div className="shell">
      <div className="app-bg" aria-hidden="true" />
      <div className="page" style={{ margin: "0 auto", width: "100%", maxWidth: 420 }}>
        <div className="row-between" style={{ marginBottom: 24 }}>
          <div className="row"><span className="brand-mark">R</span><span className="t-h2">Reto</span></div>
          <ThemeSwitch />
        </div>

        {stage === "email" ? (
          <Card>
            <h1 className="t-h1" style={{ marginBottom: 4 }}>Sign in</h1>
            <p className="muted t-small" style={{ marginBottom: 20 }}>Type your email and we'll send you a link to sign in. No password needed.</p>
            {error ? <div className="err-text" data-testid="login-error" style={{ marginBottom: 12 }}>{error}</div> : null}
            <form onSubmit={send}>
              <Field label="Email" htmlFor="email">
                <Input id="email" data-testid="login-email" type="email" autoComplete="username" required
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@shop.ph" />
              </Field>
              <Button className="btn-block tap" variant="primary" type="submit" data-testid="send-link" loading={busy}>Send my sign-in link</Button>
            </form>
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Button size="sm" data-testid="use-password" onClick={() => { setError(""); setStage("password"); }}>
                <KeyRound size={16} strokeWidth={1.75} /> Sign in with a password instead
              </Button>
            </div>
            <p className="muted-3 t-small" style={{ marginTop: 16 }}>Working at the counter? <Link to="/counter-signin" data-testid="go-counter">Sign in with your PIN</Link></p>
          </Card>
        ) : stage === "password" ? (
          <Card>
            <button className="btn btn-ghost btn-sm" data-testid="use-magic" onClick={() => { setError(""); setStage("email"); }}><ArrowLeft size={20} strokeWidth={1.75} /> Use a sign-in link instead</button>
            <h1 className="t-h1" style={{ margin: "8px 0 4px" }}>Sign in with a password</h1>
            <p className="muted t-small" style={{ marginBottom: 20 }}>Use this if the sign-in link is slow to arrive.</p>
            {error ? <div className="err-text" data-testid="password-error" style={{ marginBottom: 12 }}>{error}</div> : null}
            <form onSubmit={submitPassword}>
              <Field label="Email" htmlFor="p-email">
                <Input id="p-email" data-testid="login-email-pw" type="email" autoComplete="username" required
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@shop.ph" />
              </Field>
              <Field label="Password" htmlFor="p-password">
                <Input id="p-password" data-testid="password-input" type="password" autoComplete="current-password" required
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" />
              </Field>
              <Button className="btn-block" variant="primary" type="submit" data-testid="password-signin" loading={busy}>Sign in</Button>
            </form>
            <p className="muted-3 t-small" style={{ marginTop: 16 }}>Working at the counter? <Link to="/counter-signin" data-testid="go-counter-2">Sign in with your PIN</Link></p>
          </Card>
        ) : (
          <Card>
            <button className="btn btn-ghost btn-sm" data-testid="edit-email" onClick={() => setStage("email")}><ArrowLeft size={20} strokeWidth={1.75} /> Wrong email? Fix it</button>
            <div style={{ textAlign: "center", margin: "16px 0" }}>
              <div className="ph-icon" style={{ display: "inline-flex" }}><Mail size={20} strokeWidth={1.75} /></div>
              <h1 className="t-h2" style={{ margin: "12px 0 4px" }}>Check your email</h1>
              <p className="muted t-small">We sent a sign-in link and a 6-digit code to<br /><strong data-testid="sent-to">{email}</strong></p>
            </div>
            {error ? <div className="err-text" data-testid="code-error" style={{ marginBottom: 12 }}>{error}</div> : null}
            {devCode ? <div className="offline-banner" data-testid="dev-code" style={{ marginBottom: 12 }}><div><div className="t-small">Demo code (email is mocked)</div><div className="t-h3 num">{devCode}</div></div></div> : null}
            <form onSubmit={submitCode}>
              <Field label="Or type the 6-digit code" hint="Reading email on another device? Type the code here." htmlFor="code">
                <Input id="code" data-testid="code-input" inputMode="numeric" maxLength={6} required
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" />
              </Field>
              <Button className="btn-block" variant="primary" type="submit" data-testid="verify-code" loading={busy}>Sign in</Button>
            </form>
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <Button size="sm" data-testid="resend" disabled={resendIn > 0 || busy} onClick={() => send()}>
                {resendIn > 0 ? `Send it again in ${resendIn}s` : "Send it again"}
              </Button>
            </div>
          </Card>
        )}

        <Card style={{ marginTop: 12 }}>
          <p className="t-label muted" style={{ marginBottom: 12 }}>Demo emails (tap to fill)</p>
          <div className="stack">
            {DEMO.map((d) => (
              <div key={d.email} className="row-between">
                <span className="t-body">{d.label}</span>
                <Button size="sm" data-testid={`demo-${d.label.split(" ")[0].toLowerCase()}`} onClick={() => { setEmail(d.email); setStage("email"); }}>Use this</Button>
              </div>
            ))}
          </div>
          <p className="muted-3 t-small" style={{ marginTop: 12 }}>Email is mocked in this demo — after sending, your code shows on the next screen automatically for testing.</p>
        </Card>
      </div>
    </div>
  );
}
